<template>
  <!-- V1 Slider view if testimonials are more than 3 -->
  <section
    v-if="variant === variants.v1 && reviews?.length > 3"
    class="n-section-primary tw-overflow-hidden lg:!tw-pr-0"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <div
      class="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-y-10 lg:tw-flex-row lg:tw-items-start lg:tw-gap-10 xl:tw-gap-20"
    >
      <!-- First div (titles) with 40% width -->
      <div class="tw-w-full tw-text-start lg:tw-w-[35%]">
        <small>{{ vars.altTitleText }}</small>
        <h2>{{ vars.titleText }}</h2>
      </div>

      <!-- Second div (slider) with 60% width -->
      <div class="tw-w-full lg:tw-w-[65%]">
        <Swiper
          v-if="reviews.length > 3"
          :modules="[SwiperPagination]"
          @slideChange="onSlideChange"
          @swiper="onSwiper"
          :pagination="true"
          :loop="true"
          :slides-per-view="1"
          :space-between="24"
          effect="fade"
          :clickable="true"
          :breakpoints="{
            320: {
              slidesPerView: 1,
            },
            1024: {
              slidesPerView: 2,
            },
          }"
          class="lg:tw-w-[900px] xl:tw-w-[1100px] 2xl:tw-w-[1300px]"
        >
          <SwiperSlide v-for="(review, index) in reviews" :key="index">
            <div
              :key="index"
              class="tw-flex tw-h-full tw-min-h-[400px] tw-flex-col tw-gap-3 tw-border-[1px] tw-p-10 tw-text-start"
              :style="`border-radius: var(--rounded); border-color: var(--c-border);`"
            >
              <div class="tw-flex tw-items-center tw-gap-2">
                <svg
                  v-for="n in review.rating"
                  :key="n"
                  width="28"
                  height="28"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 1.25L12.472 7.5H19L13.764 10.972L15.236 17.5L10 13.444L4.764 17.5L6.236 10.972L1 7.5H7.528L10 1.25Z"
                    fill="var(--c-primary)"
                  />
                </svg>
              </div>
              <p class="tw-line-clamp-[9] tw-pt-4">{{ review.content }}</p>
              <p class="n-semibold tw-mt-auto">{{ review.author }}</p>
            </div>
          </SwiperSlide>
        </Swiper>
        <div class="lg:tw-hidden">
          <LibrarySliderPagination
            :swiper="swiper"
            variant="v4"
            :can-paginate-back="canPaginateBack"
            :can-paginate-next="canPaginateNext"
          />
        </div>
      </div>
    </div>
  </section>

  <!-- V1 Column view if testimonials are 3 or fewer -->
  <section
    v-if="variant === variants.v1 && reviews?.length <= 3"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <div
      class="tw-flex tw-max-w-[900px] tw-flex-col tw-items-center tw-justify-center tw-gap-3 lg:tw-gap-6 xl:tw-max-w-[1000px]"
    >
      <div class="tw-mb-10 tw-w-full tw-text-start md:tw-mb-14 md:tw-text-center">
        <small v-if="vars.altTitleText">{{ vars.altTitleText }}</small>
        <h2>{{ vars.titleText }}</h2>
      </div>
      <div v-for="(review, index) in reviews" :key="index" class="testimonial-item">
        <div class="testimonial-thumb">
          <h6>{{ review.author }}</h6>
        </div>
        <div class="testimonial-content">
          <div class="tw-mb-5 tw-flex tw-items-center tw-gap-2">
            <svg
              v-for="n in review.rating"
              :key="n"
              width="24"
              height="24"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 1.25L12.472 7.5H19L13.764 10.972L15.236 17.5L10 13.444L4.764 17.5L6.236 10.972L1 7.5H7.528L10 1.25Z"
                fill="var(--c-primary)"
              />
            </svg>
          </div>
          <p class="tw-line-clamp-[12]">
            {{ review.content }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- v1_Google -->
  <section
    v-if="variant === variants.v1_Google"
    class="n-section-primary tw-overflow-hidden"
    :style="`background-color: var(--c-${vars.backgroundColor});color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'};`"
  >
    <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-y-10 lg:tw-flex-row lg:tw-gap-14 xl:tw-gap-24">
      <div class="tw-w-full tw-text-start lg:tw-w-[40%]">
        <p>{{ vars.altTitleText }}</p>
        <h2>{{ vars.titleText }}</h2>
      </div>

      <div class="tw-w-full lg:tw-w-[60%]">
        <Swiper
          :modules="[SwiperPagination]"
          @slideChange="onSlideChange"
          @swiper="onSwiper"
          :loop="true"
          :pagination="false"
          :slides-per-view="1"
          :space-between="24"
          effect="fade"
          :clickable="true"
          :breakpoints="{
            320: {
              slidesPerView: 1,
            },
            1024: {
              slidesPerView: 3,
            },
          }"
          class="tw-w-[97%] lg:tw-w-[220%]"
        >
          <SwiperSlide v-for="(review, index) in reviewsV2" :key="index">
            <div
              :key="index"
              class="tw-flex tw-h-full tw-min-h-[350px] tw-flex-col tw-gap-3 tw-border-[0.5px] tw-p-10 tw-text-start lg:tw-min-h-[400px]"
              :style="`border-radius: var(--rounded); border-color: var(--c-border);`"
            >
              <div class="tw-flex tw-items-center tw-gap-2">
                <svg
                  v-for="n in review.evaluation_value"
                  :key="n"
                  width="28"
                  height="28"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 1.25L12.472 7.5H19L13.764 10.972L15.236 17.5L10 13.444L4.764 17.5L6.236 10.972L1 7.5H7.528L10 1.25Z"
                    fill="var(--c-primary)"
                  />
                </svg>
              </div>
              <p class="tw-line-clamp-[9] tw-pt-4">{{ review.content }}</p>
              <span class="tw-mt-auto tw-text-[var(--c-tertiary)]">{{ review.person_name }}</span>
            </div>
          </SwiperSlide>
        </Swiper>
        <div class="tw-mt-10">
          <LibrarySliderPagination
            :swiper="swiper"
            variant="v5"
            :can-paginate-back="canPaginateBack"
            :can-paginate-next="canPaginateNext"
          />
        </div>
      </div>
    </div>
  </section>

  <!-- V2 view -->
  <section
    v-if="variant === variants.v2"
    class="n-section-primary tw-overflow-hidden"
    :style="`background-color: var(--c-${vars.backgroundColor});color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'};`"
  >
    <div class="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-y-10 lg:tw-gap-14 xl:tw-gap-20">
      <div class="tw-w-full tw-text-center">
        <h2>{{ vars.titleText }}</h2>
      </div>

      <div class="tw-w-full">
        <Swiper
          :modules="[SwiperPagination]"
          @slideChange="onSlideChange"
          @swiper="onSwiper"
          :loop="true"
          :pagination="false"
          :slides-per-view="1"
          :space-between="24"
          effect="fade"
          :clickable="true"
          :breakpoints="{
            320: {
              slidesPerView: 1,
            },
            1024: {
              slidesPerView: 3,
            },
          }"
          class="tw-w-full lg:tw-w-[130%]"
        >
          <SwiperSlide v-for="(review, index) in reviewsV2" :key="index">
            <div
              :key="index"
              class="tw-flex tw-h-full tw-min-h-[390px] tw-flex-col tw-gap-3 tw-border-[0.5px] tw-p-10 tw-text-start lg:tw-min-h-[400px]"
              :style="`border-radius: var(--rounded); border-color: var(--c-border);`"
            >
              <div class="tw-flex tw-items-center tw-gap-2">
                <svg
                  v-for="n in review.evaluation_value"
                  :key="n"
                  width="28"
                  height="28"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 1.25L12.472 7.5H19L13.764 10.972L15.236 17.5L10 13.444L4.764 17.5L6.236 10.972L1 7.5H7.528L10 1.25Z"
                    fill="var(--c-primary)"
                  />
                </svg>
              </div>
              <p class="tw-line-clamp-[9] tw-pt-4">{{ review.content }}</p>
              <span class="tw-mt-auto tw-text-[var(--c-tertiary)]">{{ review.person_name }}</span>
            </div>
          </SwiperSlide>
        </Swiper>
        <div class="tw-mt-10">
          <LibrarySliderPagination
            :swiper="swiper"
            variant="v5"
            :can-paginate-back="canPaginateBack"
            :can-paginate-next="canPaginateNext"
          />
        </div>
      </div>
    </div>
  </section>

  <section
    v-if="variant === variants.v3"
    class="n-section-primary tw-overflow-hidden"
    :style="`background-color: var(--c-${vars.backgroundColor});color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text_primary)'};`"
  >
    <div class="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-y-10 lg:tw-flex-row lg:tw-gap-14 xl:tw-gap-24">
      <div class="tw-w-full tw-text-center lg:tw-w-[40%] lg:tw-text-start">
        <p>{{ vars.altTitleText }}</p>
        <h2>{{ vars.titleText }}</h2>
      </div>

      <div class="tw-w-full lg:tw-w-[60%]">
        <Swiper
          :modules="[SwiperPagination]"
          @slideChange="onSlideChange"
          @swiper="onSwiper"
          :loop="true"
          :pagination="false"
          :slides-per-view="1"
          :space-between="24"
          effect="fade"
          :clickable="true"
          :breakpoints="{
            320: {
              slidesPerView: 1,
            },
            1024: {
              slidesPerView: 3,
            },
          }"
          class="tw-w-[97%] lg:tw-w-[220%]"
        >
          <SwiperSlide v-for="(review, index) in reviewsV2" :key="index">
            <div
              :key="index"
              class="tw-flex tw-h-full tw-min-h-[350px] tw-flex-col tw-gap-3 tw-border-[1px] tw-p-10 tw-text-start lg:tw-min-h-[400px]"
              :style="`border-radius: var(--rounded); border-color: var(--c-text_primary);`"
            >
              <div class="tw-flex tw-items-center tw-gap-2">
                <svg
                  v-for="n in review.evaluation_value"
                  :key="n"
                  width="28"
                  height="28"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 1.25L12.472 7.5H19L13.764 10.972L15.236 17.5L10 13.444L4.764 17.5L6.236 10.972L1 7.5H7.528L10 1.25Z"
                    fill="var(--c-primary)"
                  />
                </svg>
              </div>
              <p class="tw-line-clamp-[9] tw-pt-4">{{ review.content }}</p>
              <h6 class="tw-mt-auto tw-uppercase tw-text-[var(--c-text_primary)]">{{ review.person_name }}</h6>
            </div>
          </SwiperSlide>
        </Swiper>
        <div class="tw-mt-10">
          <LibrarySliderPagination
            :swiper="swiper"
            variant="v5"
            :can-paginate-back="canPaginateBack"
            :can-paginate-next="canPaginateNext"
          />
        </div>
      </div>
    </div>
  </section>

   <section
    v-if="variant === variants.v4"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <div
      class="tw-flex tw-max-w-[900px] tw-flex-col tw-items-center tw-justify-center tw-gap-3 lg:tw-gap-6 xl:tw-max-w-[1000px]"
    >
      <div class="tw-mb-10 tw-w-full tw-text-start md:tw-mb-14 md:tw-text-center">
        <h6 v-if="vars.altTitleText">{{ vars.altTitleText }}</h6>
        <h2>{{ vars.titleText }}</h2>
      </div>
      <div v-for="(review, index) in reviews" :key="index" class="testimonial-item">
        <div class="testimonial-thumb">
          <h3>{{ review.author }}</h3>
        </div>
        <div class="testimonial-content">
          <div class="tw-mb-5 tw-flex tw-items-center tw-gap-2">
            <svg width="24" height="24" class="tw-mr-4" viewBox="0 0 24 24" fill="none"  xmlns="http://www.w3.org/2000/svg">
<path d="M8.36055 0.788944C5.96258 1.62082 3.89457 3.19976 2.46029 5.29382C1.026 7.38789 0.301037 9.88671 0.391883 12.4233C0.482728 14.9598 1.38459 17.4004 2.96501 19.3864C4.54543 21.3725 6.72109 22.7995 9.17243 23.4577C11.1598 23.9705 13.2419 23.993 15.2399 23.5233C17.0499 23.1168 18.7233 22.2471 20.0962 20.9996C21.5251 19.6615 22.5622 17.9592 23.0962 16.0758C23.6765 14.0277 23.7798 11.8738 23.3981 9.77957H12.2381V14.4089H18.7012C18.572 15.1473 18.2952 15.852 17.8873 16.4809C17.4795 17.1098 16.9489 17.6499 16.3274 18.0689C15.5382 18.591 14.6485 18.9423 13.7156 19.1002C12.7798 19.2742 11.82 19.2742 10.8843 19.1002C9.93591 18.9041 9.03874 18.5127 8.24993 17.9508C6.98271 17.0538 6.0312 15.7794 5.53118 14.3096C5.02271 12.8122 5.02271 11.1888 5.53118 9.69144C5.8871 8.64185 6.47549 7.6862 7.25243 6.89582C8.14154 5.97472 9.26718 5.31631 10.5058 4.99284C11.7445 4.66936 13.0484 4.69331 14.2743 5.06207C15.232 5.35605 16.1078 5.8697 16.8318 6.56207C17.5606 5.83707 18.2881 5.11019 19.0143 4.38144C19.3893 3.98957 19.7981 3.61644 20.1674 3.21519C19.0622 2.18671 17.765 1.38643 16.3499 0.860194C13.7731 -0.0754498 10.9536 -0.100594 8.36055 0.788944Z" fill="white"/>
<path d="M8.3607 0.789855C10.9536 -0.100288 13.7731 -0.0758051 16.3501 0.85923C17.7654 1.38904 19.062 2.19318 20.1657 3.22548C19.7907 3.62673 19.3951 4.00173 19.0126 4.39173C18.2851 5.11798 17.5582 5.84173 16.832 6.56298C16.1079 5.87061 15.2321 5.35696 14.2745 5.06298C13.0489 4.69293 11.7451 4.66759 10.5061 4.98975C9.26712 5.3119 8.14079 5.9691 7.2507 6.88923C6.47377 7.67961 5.88538 8.63526 5.52945 9.68486L1.64258 6.67548C3.03384 3.91653 5.44273 1.80615 8.3607 0.789855Z" fill="#E33629"/>
<path d="M0.611401 9.65605C0.820316 8.62067 1.16716 7.61798 1.64265 6.6748L5.52953 9.69168C5.02105 11.1891 5.02105 12.8124 5.52953 14.3098C4.23453 15.3098 2.9389 16.3148 1.64265 17.3248C0.452308 14.9554 0.0892746 12.2557 0.611401 9.65605Z" fill="#F8BD00"/>
<path d="M12.2381 9.77832H23.3981C23.7799 11.8726 23.6766 14.0264 23.0963 16.0746C22.5623 17.958 21.5252 19.6602 20.0963 20.9983C18.8419 20.0196 17.5819 19.0483 16.3275 18.0696C16.9494 17.6501 17.4802 17.1094 17.8881 16.4798C18.296 15.8503 18.5726 15.1448 18.7013 14.4058H12.2381C12.2363 12.8646 12.2381 11.3214 12.2381 9.77832Z" fill="#587DBD"/>
<path d="M1.64062 17.3246C2.93687 16.3246 4.2325 15.3196 5.5275 14.3096C6.02851 15.7799 6.98138 17.0544 8.25 17.9508C9.04127 18.5101 9.94037 18.8983 10.89 19.0908C11.8257 19.2648 12.7855 19.2648 13.7213 19.0908C14.6542 18.9329 15.5439 18.5816 16.3331 18.0596C17.5875 19.0383 18.8475 20.0096 20.1019 20.9883C18.7292 22.2366 17.0558 23.1068 15.2456 23.5139C13.2476 23.9836 11.1655 23.9611 9.17813 23.4483C7.60632 23.0286 6.13814 22.2888 4.86563 21.2752C3.51874 20.2059 2.41867 18.8583 1.64062 17.3246Z" fill="#319F43"/>
</svg>

            <svg
              v-for="n in review.rating"
              :key="n"
              width="24"
              height="24"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 1.25L12.472 7.5H19L13.764 10.972L15.236 17.5L10 13.444L4.764 17.5L6.236 10.972L1 7.5H7.528L10 1.25Z"
                fill="var(--c-primary)"
              />
            </svg>
          </div>
          <p class="tw-line-clamp-[12]">
            {{ review.content }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';

export default defineNuxtComponent({
  name: 'Testimonials',

  mixins: [ComponentMixin],

  data() {
    return {
      reviewsV2: null,
      swiper: null,
      canPaginateBack: true,
      canPaginateNext: true,
      variants: {
        v1: 'v1',
        v2: 'v2',
        v1_Google: 'v1_Google',
        v3: 'v3',
        v4: 'v4',
      },
    };
  },

  created() {
    if (
      this.variants.v2 === this.variant ||
      this.variants.v1_Google === this.variant ||
      this.variants.v3 === this.variant
    ) {
      const parsedReviews = this.parseFormV2(this.vars.googleReviews);
      this.reviewsV2 = parsedReviews?.reviews;
    }
  },

  mounted() {
    this.onSlideChange();
  },

  computed: {
    reviews() {
      if (this.variant === this.variants.v1 || this.variant === this.variants.v4) {
  return this.groupedVariables.reviews;
}

    },
  },

  methods: {
    onSwiper(swiper) {
      this.swiper = swiper;
    },

    onSlideChange() {
      if (this.variant === this.variants.v1) {
        if (!this.swiper) return;
        const isAtStart = this.swiper.isBeginning;
        const isAtEnd = this.swiper.isEnd;
        if (isAtStart) this.canPaginateBack = false;
        else this.canPaginateBack = true;
        if (isAtEnd) this.canPaginateNext = false;
        else this.canPaginateNext = true;
      } else if (this.variant === this.variants.v2) {
        if (!this.swiper) return;
        this.canPaginateBack = true;
        this.canPaginateNext = true;
      }
    },
  },
});
</script>

<style scoped>
.testimonial-item {
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 30px;
  gap: 25px;
}
.testimonial-item:last-child {
  margin-bottom: 0;
}
.testimonial-thumb {
  position: relative;
  width: 80px;
  flex: 0 0 85px;
  margin-right: 12px;
}
.testimonial-content {
  border: 0.5px solid var(--c-border);
  border-radius: var(--rounded);
  padding: 40px;
  position: relative;
  margin-left: 12px;
}
.testimonial-content::before {
  content: '';
  position: absolute;
  left: -9px;
  top: 50%;
  transform: translateY(-50%) rotate(-45deg);
  width: 16px;
  height: 16px;
  background: var(--c-section_primary);
  border-top: 0.5px solid var(--c-border);
  border-left: 0.5px solid var(--c-border);
}
.testimonial-item:nth-child(odd) {
  flex-direction: row-reverse;
}
.testimonial-item:nth-child(odd) .testimonial-thumb {
  margin-right: 0;
  margin-left: 22px;
}
.testimonial-item:nth-child(odd) .testimonial-content {
  margin-left: 0;
  margin-right: 12px;
}
.testimonial-item:nth-child(odd) .testimonial-content::before {
  left: auto;
  right: -9px;
  border-bottom: 0.5px solid var(--c-border);
  border-right: 0.5px solid var(--c-border);
  border-top: 0.5px solid transparent;
  border-left: 0.5px solid transparent;
}

@media screen and (max-width: 768px) {
  .testimonial-item {
    gap: 6px;
  }

  .testimonial-content {
    padding: 20px;
  }
}
</style>
